import { useState } from "react";
import { Loader2, Mail, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useEmailAuth } from "@/hooks/useEmailAuth";

const HEADLINE_FONT = "'Space Grotesk', sans-serif";

interface Props {
  open: boolean;
  onClose: () => void;
}



/**
 * Bottom-sheet email/password sign-in panel for the Android native Welcome
 * screen. New email signups are temporarily disabled — only existing users
 * can sign in here. Google sign-in remains the primary path.
 */
export function EmailAuthSheet({ open, onClose }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showResend, setShowResend] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { busy, signIn, resendConfirmation, sendPasswordReset } = useEmailAuth();

  if (!open) return null;

  const reset = () => {
    setError(null);
    setShowResend(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    reset();
    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }
    const result = await signIn(email, password);
    if (result.ok === true) {
      // AuthProvider session listener will route to /app.
      return;
    }
    setError(result.message);
    if (result.code === "email_not_confirmed") setShowResend(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm">
      <div
        className="w-full max-w-md rounded-t-3xl border border-white/10 bg-[#15151a] p-6 pb-8 text-[#e7e4ec] shadow-[0_-20px_60px_-10px_rgba(172,138,255,0.4)]"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)" }}
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/20" />
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-bold uppercase tracking-[0.18em] text-white" style={{ fontFamily: HEADLINE_FONT }}>
            <Mail className="mr-2 inline-block h-4 w-4 text-[#ac8aff]" />
            Sign in
          </h2>
          <button
            type="button"
            onClick={() => {
              reset();
              onClose();
            }}
            className="rounded-full p-1.5 text-[#acaab1] hover:bg-white/5 hover:text-white"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p
          className="mb-4 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-[#bfa5ff]"
          style={{ fontFamily: HEADLINE_FONT }}
        >
          New email signups are paused — sign in with Google or your existing account.
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.2em] text-[#76757b]" style={{ fontFamily: HEADLINE_FONT }}>
              Email
            </label>
            <Input
              type="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim())}
              placeholder="you@example.com"
              className="h-12 rounded-xl border-white/10 bg-white/[0.04] text-white placeholder:text-[#76757b]"
              required
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.2em] text-[#76757b]" style={{ fontFamily: HEADLINE_FONT }}>
              Password
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              className="h-12 rounded-xl border-white/10 bg-white/[0.04] text-white placeholder:text-[#76757b]"
              required
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
              {error}
              {showResend && (
                <button
                  type="button"
                  onClick={() => resendConfirmation(email)}
                  className="ml-1 underline underline-offset-2 hover:text-red-200"
                >
                  Resend verification
                </button>
              )}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            className="h-12 w-full rounded-2xl bg-gradient-to-r from-[#ac8aff] to-[#8f60fa] text-xs font-bold uppercase tracking-[0.22em] text-[#0e0e11] hover:from-[#bfa5ff] hover:to-[#ac8aff] disabled:opacity-70"
            style={{ fontFamily: HEADLINE_FONT }}
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Please wait…
              </>
            ) : (
              "Sign in"
            )}
          </Button>

          <button
            type="button"
            onClick={() => {
              if (!email) {
                setError("Enter your email above first.");
                return;
              }
              sendPasswordReset(email);
            }}
            className="block w-full pt-1 text-center text-[10px] font-semibold uppercase tracking-[0.22em] text-[#bfa5ff] hover:text-white"
            style={{ fontFamily: HEADLINE_FONT }}
          >
            Forgot password?
          </button>
        </form>
      </div>
    </div>
  );
}
